import './FAQ.scss'
import { useState, useCallback } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useRestClient } from '@/contexts/QueryProvider'
import { Loading } from '@/ui-legacy'
import { useSessionStorage } from '@/hooks'
import { isSuperUser, isApplicationAdmin } from '@/util'
import { MDXEditorComponent } from '@/components/form-components/MDXEditorComponent'
import { Button } from '@/components/shadcn/button'

type FaqProps = {
  appId: number | null
}

const FAQ = ({ appId }: FaqProps) => {
  const { getMarkdownApiGetFaq, postMarkdownApiSaveFaq } = useRestClient()
  const { data, isPending, isError, refetch } = getMarkdownApiGetFaq.useQuery(
    { path: { applicationId: appId! } },
    { enabled: appId != null }
  )
  const { mutateAsync: saveFaq, isPending: isSaving } = postMarkdownApiSaveFaq.useMutation({
    path: { applicationId: appId! },
  })

  const [roles] = useSessionStorage('roles', 0)
  const canEdit = isSuperUser(roles) || isApplicationAdmin(roles)

  const content = data?.content ?? ''
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')

  const openEditing = useCallback(() => {
    setEditValue(content)
    setIsEditing(true)
  }, [content])

  const cancelEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleSave = useCallback(async () => {
    await saveFaq({ content: editValue })
    await refetch()
    setIsEditing(false)
  }, [saveFaq, editValue, refetch])

  if (isPending) return <Loading />
  if (isError) return <div className="p-4 text-sm text-muted-foreground">Failed to load FAQ.</div>

  return (
    <div className="ux-faq-layout">
      {canEdit && (
        <div className="flex items-center justify-end gap-2 border-b px-4 py-2">
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEditing}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={openEditing}>
              Edit
            </Button>
          )}
        </div>
      )}

      {isEditing ? (
        <div className="p-4">
          <MDXEditorComponent
            markdown={editValue}
            onChange={setEditValue}
            contentEditableClassName="min-h-96 bg-background"
            autoFocus
          />
        </div>
      ) : (
        <div className="prose prose-sm max-w-none p-4">
          <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
        </div>
      )}
    </div>
  )
}

export default FAQ
